import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CalendarClock, Loader2, Info, TrendingUp } from "lucide-react";

const BUCKET_COLORS = { "0-7": "#ef4444", "8-30": "#f59e0b", "31-60": "#2563eb", "61-90": "#0891b2", "90+": "#7c3aed" };

function Bars({ dist, lang }) {
  const L = (it, en) => (lang === "en" ? en : it);
  if (!dist || !dist.samples) return <p className="text-xs text-muted-foreground">{L("Dati insufficienti per questo mese.", "Not enough data for this month.")}</p>;
  return (
    <div className="space-y-1.5">
      {dist.distribution.map((b) => (
        <div key={b.bucket} className="flex items-center gap-2 text-xs">
          <span className="w-14 text-right text-muted-foreground">{b.bucket} gg</span>
          <div className="flex-1 h-5 rounded bg-muted overflow-hidden">
            <div className="h-full rounded flex items-center justify-end pr-1.5 text-[10px] font-bold text-white"
              style={{ width: `${Math.max(b.pct, b.pct > 0 ? 6 : 0)}%`, backgroundColor: BUCKET_COLORS[b.bucket] }}>
              {b.pct > 0 ? `${b.pct}%` : ""}
            </div>
          </div>
          <span className="w-8 text-muted-foreground">{b.count}</span>
        </div>
      ))}
    </div>
  );
}

export default function BookingPace({ propId, lang }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mo, setMo] = useState(new Date().getMonth() + 1);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/properties/${propId}/booking-pace?lang=${lang}`); setD(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [propId, lang]);
  useEffect(() => { load(); }, [load]);

  const Head = (
    <div className="flex items-center gap-2 mb-1"><CalendarClock className="w-5 h-5 text-primary" />
      <h2 className="text-lg font-semibold">{L("Anticipo della domanda", "Demand pace")}</h2></div>
  );

  if (loading && !d) return <div className="bg-card border border-border rounded-lg p-6"><Loader2 className="w-5 h-5 animate-spin inline text-muted-foreground" /></div>;
  if (!d) return null;

  if (!d.has_data) {
    return (
      <div className="bg-card border border-border rounded-lg p-6" data-testid="booking-pace">
        {Head}
        <div className="mt-3 flex items-start gap-2 text-sm text-muted-foreground bg-amber-500/10 rounded-lg p-3" data-testid="pace-empty">
          <Info className="w-4 h-4 mt-0.5 text-amber-500 flex-shrink-0" />{d.message}
        </div>
      </div>
    );
  }

  const row = d.months.find((x) => x.month === mo) || d.months[0];
  const own = row.own;
  const zone = row.zone;
  const headline = own?.peak_bucket
    ? L(`La domanda per ${row.label} parte soprattutto ${own.peak_bucket} giorni prima (lead time medio ${own.median_lead} gg).`,
        `Demand for ${row.label} starts mostly ${own.peak_bucket} days ahead (median lead ${own.median_lead} d).`)
    : L(`Dati insufficienti per ${row.label}.`, `Not enough data for ${row.label}.`);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-lg p-6 space-y-4" data-testid="booking-pace">
      <div className="flex items-center justify-between flex-wrap gap-3">
        {Head}
        <select value={mo} onChange={(e) => setMo(Number(e.target.value))} data-testid="pace-month"
          className="h-9 rounded-md border border-border bg-background px-2 text-sm">
          {d.months.map((x) => <option key={x.month} value={x.month}>{x.label}</option>)}
        </select>
      </div>

      {own?.peak_bucket && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm flex items-start gap-2" data-testid="pace-headline">
          <TrendingUp className="w-4 h-4 text-primary mt-0.5" />{headline}
        </div>
      )}

      {(d.reminders || []).length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-1.5" data-testid="pace-reminders">
          <p className="text-xs font-bold uppercase tracking-wide text-amber-700 flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" />{L("Promemoria prezzi", "Pricing reminders")}</p>
          {d.reminders.map((r, i) => (
            <div key={i} className="text-sm flex items-start gap-2" data-testid={`pace-reminder-${r.month}`}>
              <span className="text-amber-500 mt-0.5">•</span>{r.text}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <p className="text-sm font-semibold mb-2">{L("Il tuo storico", "Your history")} <span className="text-xs text-muted-foreground">({own?.samples || 0} {L("pren.", "bk")})</span></p>
          <Bars dist={own} lang={lang} />
        </div>
        <div>
          <p className="text-sm font-semibold mb-2">{L("Zona (anonimo)", "Zone (anonymous)")}</p>
          {d.zone.available
            ? <Bars dist={zone} lang={lang} />
            : <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3" data-testid="pace-zone-unavailable">
                {L(`Dati di zona non disponibili: servono almeno ${d.zone.min_required} strutture nella micro-zona con la data di prenotazione (attuali: ${d.zone.properties}).`,
                   `Zone data unavailable: at least ${d.zone.min_required} structures in the micro-zone with booking dates are required (current: ${d.zone.properties}).`)}
              </p>}
        </div>
      </div>

      <div className="rounded-lg border border-border p-3 flex items-center justify-between text-sm" data-testid="pace-onbooks">
        <span className="text-muted-foreground">{L(`Prenotazioni già acquisite per ${row.label} (pace attuale)`, `On-the-books for ${row.label} (current pace)`)}</span>
        <span className="text-xl font-bold">{row.on_the_books}</span>
      </div>
    </motion.div>
  );
}
